import L from 'leaflet';
import { useMemo } from 'react';
import { Marker } from 'react-leaflet';
import type { VehiclePosition } from '../index';
import type { ILine } from '../../../../models/line.model';
import Colors from '../../../../helpers/colors.helper';
import { LineHelper } from '../../../../helpers/line.helper';

// Unlike JSX, which escapes values automatically, the SVG is built as a raw string and
// injected into the DOM via Leaflet's divIcon({ html }), bypassing React's escaping.
// escapeXml() sanitises the label; safeColor() validates color values before interpolation.
function escapeXml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Color values are interpolated into raw SVG attribute strings (not React JSX), so they
// are not automatically escaped. This guard rejects anything that isn't a valid CSS color
// token, preventing attribute break-out if a value ever arrives from an untrusted source.
function safeColor(color: string): string {
  if (!/^[#a-zA-Z0-9(),%. -]+$/.test(color)) throw new Error(`Invalid color value: ${color}`);
  return color;
}

const ICON_HIT_AREA = 44; // WCAG 2.5.5 minimum touch target (44×44 px)

// ─── SVG PATH DEFINITIONS ───────────────────────────────────────────────────
// One function per icon state. Edit these when the designer provides a new SVG.
// The tail is east-pointing in both designs; buildDivIcon handles pre-rotation to south.

/** Unselected icon — 41×34 viewBox, circle centre (17, 17). */
function buildUnselectedPaths(backgroundColor: string, outerBorderColor: string, hasTail: boolean): string {
  const bg = safeColor(backgroundColor);
  const border = safeColor(outerBorderColor);
  const tail = hasTail
    ? `<path d="M40.999 17L30.999 23V11L40.999 17Z" fill="white"/>` + `<path d="M31 11L41 17L31 23V11ZM37.1133 17L33 14.5332L30 12.7451L31 17L30 21.2451L33 19.4678L37.1133 17Z" fill="${border}"/>`
    : '';
  return (
    `<path d="M33 17C33 25.8366 25.8366 33 17 33C8.16344 33 1 25.8366 1 17C1 8.16344 8.16344 1 17 1C25.8366 1 33 8.16344 33 17Z" stroke="${border}" stroke-width="2"/>` +
    tail +
    `<path d="M17 32C8.71573 32 2 25.2843 2 17C2 8.71573 8.71573 2 17 2C25.2843 2 32 8.71573 32 17C32 25.2843 25.2843 32 17 32Z" fill="white"/>` +
    `<path d="M17 30C9.8203 30 4 24.1797 4 17C4 9.8203 9.8203 4 17 4C24.1797 4 30 9.8203 30 17C30 24.1797 24.1797 30 17 30Z" fill="${bg}"/>`
  );
}

/** Selected icon — 50×40 viewBox, circle centre (20, 20). */
function buildSelectedPaths(backgroundColor: string, outerBorderColor: string, hasTail: boolean): string {
  const bg = safeColor(backgroundColor);
  const border = safeColor(outerBorderColor);
  const tail = hasTail ? `<path d="M49.999 20L37.499 27.5V12.5L49.999 20Z" fill="${border}"/>` : '';
  return (
    tail +
    `<path d="M38.125 20C38.125 30.0102 30.0102 38.125 20 38.125C9.98984 38.125 1.875 30.0102 1.875 20C1.875 9.98984 9.98984 1.875 20 1.875C30.0102 1.875 38.125 9.98984 38.125 20Z" fill="${bg}" stroke="${border}" stroke-width="3.75"/>`
  );
}

// ─── SVG ASSEMBLY ───────────────────────────────────────────────────────────
// Shared mechanics: rotation, label counter-rotation, Leaflet divIcon wrapper.
// The tail in both designs points east; a 90° CW pre-rotation on the <g> aligns
// it to south so that the course-based outer rotation works correctly.

interface DivIconOptions {
  paths: string;
  width: number;
  height: number;
  viewBox: string;
  cx: number; // circle centre x in SVG coordinate space
  cy: number; // circle centre y in SVG coordinate space
  rotation: number;
  label: string | undefined;
  textColor: string;
}

function buildDivIcon({ paths, width, height, viewBox, cx, cy, rotation, label, textColor }: DivIconOptions): L.DivIcon {
  const fontSize = label && label.length >= 4 ? 8 : label && label.length >= 3 ? 10 : 11;
  const textEl = label
    ? `<text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central" fill="${textColor}" font-size="${fontSize}" font-weight="bold" transform="rotate(${-rotation}, ${cx}, ${cy})">${escapeXml(label)}</text>`
    : '';
  const rotateStyle = `transform:rotate(${rotation}deg);transform-origin:${cx}px ${cy}px;overflow:visible;`;
  const svg =
    `<svg width="${width}" height="${height}" viewBox="${viewBox}" fill="none" xmlns="http://www.w3.org/2000/svg" style="${rotateStyle}">` +
    `<g transform="rotate(90, ${cx}, ${cy})">` +
    paths +
    `</g>` +
    textEl +
    `</svg>`;
  const hitPadX = ICON_HIT_AREA / 2 - cx;
  const hitPadY = ICON_HIT_AREA / 2 - cy;
  // The wrapper must be at least ICON_HIT_AREA × ICON_HIT_AREA (WCAG 2.5.5 touch target),
  // but wide/tall enough to contain the SVG without clipping it.
  // The tail intentionally overflows below the wrapper; overflow:visible is explicit.
  const wrapperWidth = Math.max(ICON_HIT_AREA, hitPadX + width);
  const wrapperHeight = Math.max(ICON_HIT_AREA, hitPadY + height);
  return L.divIcon({
    html:
      `<div style="position:relative;width:${wrapperWidth}px;height:${wrapperHeight}px;overflow:visible">` +
      `<div style="position:absolute;left:${hitPadX}px;top:${hitPadY}px">${svg}</div>` +
      `</div>`,
    className: '',
    iconSize: [wrapperWidth, wrapperHeight],
    iconAnchor: [hitPadX + cx, hitPadY + cy],
  });
}

// ─── ICON FACTORY ───────────────────────────────────────────────────────────

function createVehicleIcon(label: string | undefined, backgroundColor: string, outerBorderColor: string, textColor: string, isSelected: boolean, course: number | null): L.DivIcon {
  const rotation = course !== null ? course - 180 : 0;
  const hasTail = course !== null;

  if (!isSelected)
    return buildDivIcon({
      paths: buildUnselectedPaths(backgroundColor, outerBorderColor, hasTail),
      width: 41,
      height: 34,
      viewBox: '0 0 41 34',
      cx: 17,
      cy: 17,
      rotation,
      label,
      textColor,
    });

  return buildDivIcon({
    paths: buildSelectedPaths(backgroundColor, outerBorderColor, hasTail),
    width: 50,
    height: 40,
    viewBox: '0 0 50 40',
    cx: 20,
    cy: 20,
    rotation,
    label,
    textColor,
  });
}

interface IVehicleMarkerProps {
  vehicle: VehiclePosition;
  line: ILine | undefined;
  isSelected: boolean;
  isDeselected: boolean;
  onSelect: () => void;
  onHover?: () => void;
  onHoverEnd?: () => void;
}

export default function VehicleMarker({ vehicle, line, isSelected, isDeselected, onSelect, onHover, onHoverEnd }: IVehicleMarkerProps) {
  const backgroundColor = isDeselected ? Colors.vtdGray100 : line ? LineHelper.getBackgroundColor(line) : Colors.vtdBlue1000;
  const outerBorderColor = isSelected ? Colors.vtdBlue1000 : Colors.vtdGray700;
  const textColor = isDeselected ? Colors.vtdGray900 : line ? LineHelper.getForegroundColor(line) : Colors.vtdWhite;
  const label = line ? LineHelper.getLineName(line, false, true) : '';
  const icon = useMemo(
    () => createVehicleIcon(label, backgroundColor, outerBorderColor, textColor, isSelected, vehicle.course),
    [label, backgroundColor, outerBorderColor, textColor, isSelected, vehicle.course],
  );

  if (vehicle.latitude === null || vehicle.longitude === null) return null;

  return (
    <Marker
      position={[vehicle.latitude, vehicle.longitude]}
      icon={icon}
      zIndexOffset={isSelected ? 2200 : 1200}
      eventHandlers={{
        click: (e) => {
          L.DomEvent.stopPropagation(e.originalEvent); // Prevent the map's click handler from also firing, which would immediately deselect the vehicle again.
          onSelect();
        },
        mouseover: (e) => {
          L.DomEvent.stopPropagation(e.originalEvent);
          onHover?.();
        },
        mouseout: (e) => {
          L.DomEvent.stopPropagation(e.originalEvent);
          onHoverEnd?.();
        },
      }}
    />
  );
}
